import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2 } from 'lucide-react';

interface EmailViewerProps {
  isOpen: boolean;
  onClose: () => void;
  emailUrl: string | null;
  inquiryName?: string;
}

export function EmailViewer({ isOpen, onClose, emailUrl, inquiryName }: EmailViewerProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && emailUrl) {
      setLoading(true);
      setError(null);
    }
  }, [isOpen, emailUrl]);

  const handleIframeLoad = () => {
    setLoading(false);
  };

  const handleIframeError = () => {
    setLoading(false);
    setError('Failed to load email. The link may have expired.');
  };

  const handleOpenInNewTab = () => {
    if (emailUrl) {
      window.open(emailUrl, '_blank');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            Original Email {inquiryName && `- ${inquiryName}`}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 relative border rounded-lg overflow-hidden bg-gray-50">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin text-aalaw-red mx-auto mb-2" />
                <p className="text-sm text-gray-600">Loading email...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
              <div className="text-center max-w-md">
                <p className="text-red-600 mb-4">{error}</p>
                <Button onClick={handleOpenInNewTab} variant="outline">
                  Try Opening in New Tab
                </Button>
              </div>
            </div>
          )}

          {emailUrl && (
            <iframe
              src={emailUrl}
              className="w-full h-full"
              title="Email Content"
              sandbox="allow-same-origin"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
            />
          )}
        </div>

        <div className="flex justify-between items-center pt-4 border-t">
          <Button onClick={handleOpenInNewTab} variant="outline" size="sm">
            Open in New Tab
          </Button>
          <Button onClick={onClose} variant="default">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
